/* eslint-disable max-lines */
import React, {
 useState
} from 'react';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 TooltipProvider, Tooltip, TooltipTrigger, TooltipContent
} from 'shared/components/ui/tooltip';
import {
 Info
} from 'lucide-react';

interface ConnectionSettings {
    base_url?: string;
    method?: string;
    path?: string;
    content_type?: string;
    timeout?: number;
    auth_type?: string;
    basic_username?: string;
    basic_password?: string;
    bearer_token?: string;
    api_key_name?: string;
    api_key_value?: string;
    api_key_in?: string;
    oauth2_client_id?: string;
    oauth2_client_secret?: string;
    oauth2_token_url?: string;
    headers?: Record<string, any>;
    query_params?: Record<string, any>;
    body_template?: Record<string, any>;
}

interface RestApiFieldsProps {
    type: 'rest'; // Only type 'rest' for REST API
    connection: { settings?: ConnectionSettings };
}

const inputStyles = 'focus-visible:ring-neutral-950 dark:focus-visible:ring-neutral-300';

// Tooltip content
const tooltips = {
    base_url: 'The base URL of your API endpoint, e.g., https://api.example.com',
    method: 'Select the HTTP method to use for the API request (GET, POST, etc.).',
    path: 'The specific endpoint path to append to the base URL.',
    content_type: 'The content type of the request, e.g., application/json',
    timeout: 'The amount of time (in seconds) to wait for the API request before timing out.',
    auth_type: 'Choose the type of authentication to use (none, basic, bearer, etc.).',
    basic_username: 'The username for Basic Authentication.',
    basic_password: 'The password for Basic Authentication.',
    bearer_token: 'The token to use for Bearer Authentication.',
    api_key_name: 'The name of the API key header, e.g., X-API-KEY.',
    api_key_value: 'The value of the API key to include in the request.',
    api_key_in: 'The location of the API key (header or query parameter).',
    oauth2_client_id: 'The Client ID for OAuth2 authentication.',
    oauth2_client_secret: 'The Client Secret for OAuth2 authentication.',
    oauth2_token_url: 'The URL used to obtain the OAuth2 token.',
    headers: 'Custom headers to include in the API request, formatted as JSON.',
    query_params: 'Query parameters to include in the API request, formatted as JSON.',
    body_template: 'The body template for the request, formatted as JSON.',
};

export const RestApiFields: React.FC<RestApiFieldsProps> = ({
 connection
}) => {
    const settings = connection?.settings || {};
    const [authType, setAuthType] = useState(settings.auth_type || 'none');

    return (
        <TooltipProvider>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Base URL */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="base_url" className="select-none text-xs">
                                Base URL
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.base_url}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="base_url"
                        name="base_url"
                        placeholder="https://api.example.com"
                        required
                        className={inputStyles}
                        defaultValue={settings.base_url}
                    />
                </div>

                {/* HTTP Method */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="method" className="select-none text-xs">
                                HTTP Method
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.method}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Select name="method" defaultValue={settings.method || 'GET'}>
                        <SelectTrigger className={inputStyles}>
                            <SelectValue placeholder="Select Method" />
                        </SelectTrigger>
                        <SelectContent>
                            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'].map((m) => (
                                <SelectItem key={m} value={m}>
                                    {m}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Endpoint Path */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="path" className="select-none text-xs">
                                Endpoint Path
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.path}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="path"
                        name="path"
                        placeholder="/data"
                        className={inputStyles}
                        defaultValue={settings.path || '/'}
                    />
                </div>

                {/* Content Type */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="content_type" className="select-none text-xs">
                                Content Type
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.content_type}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="content_type"
                        name="content_type"
                        placeholder="application/json"
                        className={inputStyles}
                        defaultValue={settings.content_type}
                    />
                </div>

                {/* Timeout */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="timeout" className="select-none text-xs">
                                Timeout (in seconds)
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.timeout}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="timeout"
                        name="timeout"
                        type="number"
                        className={inputStyles}
                        defaultValue={settings.timeout || 30}
                    />
                </div>

                {/* Auth Type */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="auth_type" className="select-none text-xs">
                                Auth Type
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.auth_type}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Select name="auth_type" value={authType} onValueChange={setAuthType}>
                        <SelectTrigger className={inputStyles}>
                            <SelectValue placeholder="Select Auth Type" />
                        </SelectTrigger>
                        <SelectContent>
                            {['none', 'basic', 'bearer', 'api_key', 'oauth2'].map((a) => (
                                <SelectItem key={a} value={a}>
                                    {a}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Optional Auth Fields */}
                {authType === 'basic' && (
                    <>
                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="basic_username" className="select-none text-xs">
                                        Basic Auth Username
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.basic_username}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="basic_username"
                                name="basic_username"
                                placeholder="e.g., user123"
                                className={inputStyles}
                                defaultValue={settings.basic_username}
                            />
                        </div>

                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="basic_password" className="select-none text-xs">
                                        Basic Auth Password
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.basic_password}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="basic_password"
                                name="basic_password"
                                type="password"
                                className={inputStyles}
                                defaultValue={settings.basic_password}
                            />
                        </div>
                    </>
                )}

                {authType === 'bearer' && (
                    <div className="space-y-2">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Label htmlFor="bearer_token" className="select-none text-xs">
                                    Bearer Token
                                    <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                </Label>
                            </TooltipTrigger>
                            <TooltipContent className="w-[300px]">
                                <p>{tooltips.bearer_token}</p>
                            </TooltipContent>
                        </Tooltip>
                        <Input
                            id="bearer_token"
                            name="bearer_token"
                            className={inputStyles}
                            defaultValue={settings.bearer_token}
                        />
                    </div>
                )}

                {/* API Key Fields */}
                {authType === 'api_key' && (
                    <>
                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="api_key_name" className="select-none text-xs">
                                        API Key Header Name
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.api_key_name}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="api_key_name"
                                name="api_key_name"
                                placeholder="e.g., X-API-KEY"
                                className={inputStyles}
                                defaultValue={settings.api_key_name}
                            />
                        </div>

                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="api_key_value" className="select-none text-xs">
                                        API Key Value
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.api_key_value}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="api_key_value"
                                name="api_key_value"
                                type="password"
                                className={inputStyles}
                                defaultValue={settings.api_key_value}
                            />
                        </div>

                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="api_key_in" className="select-none text-xs">
                                        Send API Key In
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.api_key_in}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Select name="api_key_in" defaultValue={settings.api_key_in || 'header'}>
                                <SelectTrigger className={inputStyles}>
                                    <SelectValue placeholder="Select location" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="header">Header</SelectItem>
                                    <SelectItem value="query">Query Parameter</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </>
                )}

                {/* OAuth2 Fields */}
                {authType === 'oauth2' && (
                    <>
                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="oauth2_client_id" className="select-none text-xs">
                                        OAuth2 Client ID
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.oauth2_client_id}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="oauth2_client_id"
                                name="oauth2_client_id"
                                className={inputStyles}
                                defaultValue={settings.oauth2_client_id}
                            />
                        </div>

                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="oauth2_client_secret" className="select-none text-xs">
                                        OAuth2 Client Secret
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.oauth2_client_secret}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="oauth2_client_secret"
                                name="oauth2_client_secret"
                                type="password"
                                className={inputStyles}
                                defaultValue={settings.oauth2_client_secret}
                            />
                        </div>

                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="oauth2_token_url" className="select-none text-xs">
                                        OAuth2 Token URL
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.oauth2_token_url}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="oauth2_token_url"
                                name="oauth2_token_url"
                                placeholder="https://..."
                                className={inputStyles}
                                defaultValue={settings.oauth2_token_url}
                            />
                        </div>
                    </>
                )}

                {/* Custom Headers */}
                <div className="space-y-2 md:col-span-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="headers" className="select-none text-xs">
                                Custom Headers (JSON)
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.headers}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Textarea
                        id="headers"
                        name="headers"
                        className={inputStyles}
                        placeholder='e.g., {"X-Custom": "value"}'
                        defaultValue={JSON.stringify(settings.headers || {}, null, 2)}
                        rows={4}
                    />
                </div>

                {/* Query Params */}
                <div className="space-y-2 md:col-span-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="query_params" className="select-none text-xs">
                                Query Params (JSON)
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.query_params}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Textarea
                        id="query_params"
                        name="query_params"
                        className={inputStyles}
                        placeholder='e.g., {"q": "search"}'
                        defaultValue={JSON.stringify(settings.query_params || {}, null, 2)}
                        rows={4}
                    />
                </div>

                {/* Body Template */}
                <div className="space-y-2 md:col-span-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="body_template" className="select-none text-xs">
                                Body Template (JSON)
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.body_template}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Textarea
                        id="body_template"
                        name="body_template"
                        className={inputStyles}
                        placeholder='e.g., {"name": "{{input.name}}"}'
                        defaultValue={JSON.stringify(settings.body_template || {}, null, 2)}
                        rows={4}
                    />
                </div>
            </div>
        </TooltipProvider>
    );
};
